import React from 'react';

const ItemRowDasboardLeadDia = ({ date, events, valoresKPIs }) => {
  const workersInDay = Object.entries(events[date]).filter(([_, workerData]) => {
    return workerData.worker?.area?.toUpperCase() !== 'ADMINISTRACION';
  });

  // Extraer valores KPI
  const getKpiValue = (subestado) => {
    return valoresKPIs.find((kpi) => kpi.subestado === subestado)?.valor || 0;
  };

  const kpiValues = {
    perdidaLead: getKpiValue('Llamada perdida'),
    llamadaCorta: getKpiValue('Llamada corta'),
    llamadaFria: getKpiValue('Llamada fria'),
    llamadaAtendida: getKpiValue('Llamada atendida'),
    meetCancelado: getKpiValue('Meet cancelado'),
    meetReagendado: getKpiValue('Meet reagendado'),
    meetAgendado: getKpiValue('Meet agendado'),
    meetAtendido: getKpiValue('Meet atendido'),
    exito: getKpiValue('Exito'),
    evaluandoOpciones: getKpiValue('Evaluando opciones'),
    paraElFuturo: getKpiValue('Para el futuro'),
    frio: getKpiValue('Frio'),
    porConfirmarDeposito: getKpiValue('Por confirmar depósito'),
    curioso: getKpiValue('Curioso'),
    noResponde: getKpiValue('No responde'),
    servicioCaro: getKpiValue('El servicio le parece caro'),
    otraAgencia: getKpiValue('Decidió trabajar con otra agencia'),
    freelancer: getKpiValue('Decidió trabajar con un freelancer'),
    noEnCiudad: getKpiValue('No esta en mi ciudad'),
    competencia: getKpiValue('Competencia o sospechoso'),
    pocoPresupuesto: getKpiValue('Lead con poco presupuesto')
  };

  // Función para calcular eventos por tipo
  const calculateEventsByType = (workerData, eventType, eventSubType) => {
    return workerData.events.filter(e => e.eventType === eventType && e.eventSubType === eventSubType);
  };

  // Función para calcular cotizaciones únicas
  const calculateUniqueQuotes = (events, subestados) => {
    const eventosFiltrados = events.filter(evento => 
      evento.eventType === "Cambio estado cotización" && 
      subestados.includes(evento.eventSubType)
    );
    return [...new Set(eventosFiltrados.map(evento => evento.quote?.id))];
  };

  // Función para calcular ventas totales
  const calculateTotalSales = (events, subestados) => {
    const eventosFiltrados = events.filter(evento => 
      evento.eventType === "Cambio estado cotización" && 
      subestados.includes(evento.eventSubType)
    );
    const quotesUnicos = [...new Set(eventosFiltrados.map(evento => evento.quote?.id))];
    return quotesUnicos.reduce((acc, quoteId) => {
      const evento = eventosFiltrados.find(e => e.quote?.id === quoteId);
      return acc + (evento?.quote?.precioTotalSinIGV || 0);
    }, 0);
  };

  // Función para calcular tasas de cierre
  const calculateClosureRate = (totalEntradas, cotizaciones) => {
    return totalEntradas > 0 ? (cotizaciones / totalEntradas) * 100 : 0;
  };

  return (
    <React.Fragment key={date}>
      {/* Divisor de fecha */}
      <tr className="bg-gray-50 border-t-2 border-gray-300">
        <td colSpan={32} className="px-4 py-2 font-bold text-gray-700">
          {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </td>
      </tr>

      {(() => {
        const totalEntradasGlobal = workersInDay.reduce((total, [_, workerData]) => {
          return total + workerData.events.filter(e => e.eventType === 'Entrada al sistema' && e.lead.entryOrigin === "Kommo").length;
        }, 0);

        return workersInDay.map(([workerId, workerData], index) => {
          // Calcular eventos por tipo
          const llamadasPerdidas = calculateEventsByType(workerData, 'Llamada', 'Llamada perdida');
          const llamadasCortas = calculateEventsByType(workerData, 'Llamada', 'Llamada corta');
          const llamadasFrias = calculateEventsByType(workerData, 'Llamada', 'Llamada fria');
          const llamadasAtendidas = calculateEventsByType(workerData, 'Llamada', 'Llamada atendida');
          const totalLlamadas = llamadasPerdidas.length + llamadasCortas.length + llamadasFrias.length + llamadasAtendidas.length;

          const meetAgendados = calculateEventsByType(workerData, 'Google Meet', 'Meet agendado');
          const meetAtendidos = calculateEventsByType(workerData, 'Google Meet', 'Meet atendido');
          const meetReagendados = calculateEventsByType(workerData, 'Google Meet', 'Meet reagendado');
          const meetCancelados = calculateEventsByType(workerData, 'Google Meet', 'Meet cancelado');
          const totalMeet = meetAgendados.length + meetAtendidos.length + meetReagendados.length + meetCancelados.length;

          // Calcular cotizaciones
          const eventosCotizacionesEnEspera = workerData.events.filter(evento => 
            evento.eventType === "Cambio estado cotización" && 
            ["Frio", "Evaluando opciones", "Por confirmar depósito", "Para el futuro"].includes(evento.eventSubType)
          );
          const eventosCotizacionesEnEsperaUnicos = calculateUniqueQuotes(eventosCotizacionesEnEspera, ["Frio", "Evaluando opciones", "Por confirmar depósito", "Para el futuro"]);

          const quotesEnExito = workerData.events.filter(evento => 
            evento.eventType === "Cambio estado cotización" && 
            evento.eventSubType === "Exito"
          );
          const eventosCotizacionesEnExitoUnicos = calculateUniqueQuotes(quotesEnExito, ["Exito"]);

          // Calcular ventas
          const ventasProyectadas = calculateTotalSales(workerData.events, ["Frio", "Evaluando opciones", "Por confirmar depósito", "Para el futuro"]);
          const ventasRealizadas = calculateTotalSales(workerData.events, ["Exito"]);

          // Calcular KPIs
          const totalKPI = (
            // Llamadas
            (llamadasPerdidas.length * kpiValues.perdidaLead) + 
            (llamadasCortas.length * kpiValues.llamadaCorta) + 
            (llamadasFrias.length * kpiValues.llamadaFria) + 
            (llamadasAtendidas.length * kpiValues.llamadaAtendida) + 
        
            // Google Meet
            (meetAgendados.length * kpiValues.meetAgendado) + 
            (meetAtendidos.length * kpiValues.meetAtendido) + 
            (meetReagendados.length * kpiValues.meetReagendado) + 
            (meetCancelados.length * kpiValues.meetCancelado) + 
        
            // Cotizaciones en espera
            (eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Frio').length * kpiValues.frio) + 
            (eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Evaluando opciones').length * kpiValues.evaluandoOpciones) + 
            (eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Por confirmar depósito').length * kpiValues.porConfirmarDeposito) + 
            (eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Para el futuro').length * kpiValues.paraElFuturo) + 
        
            // Cotizaciones en exito
            (quotesEnExito.length * kpiValues.exito)
          );

          const totalEntradasWorker = workerData.events.filter(e => e.eventType === 'Entrada al sistema' && e.lead.entryOrigin === "Kommo" && e.seller?.id === workerId).length;

          // Calcular tasas de cierre
          const tasaCierreCotizacionGlobal = calculateClosureRate(totalEntradasGlobal, eventosCotizacionesEnEsperaUnicos.length);
          const tasaCierreCotizacionIndividual = calculateClosureRate(totalEntradasWorker, eventosCotizacionesEnEspera.length);
          const tasaCierreExitoGlobal = calculateClosureRate(totalEntradasGlobal, eventosCotizacionesEnExitoUnicos.length);
          const tasaCierreExitoIndividual = calculateClosureRate(totalEntradasWorker, quotesEnExito.length);

          return (
            <tr 
              key={`${date}-${workerId}`} 
              className="text-xs transition-all duration-100 border border-gray-200"
            >
              <td className={`${!workerData.worker?.name ? 'text-gray-200' : 'text-black'} px-2 py-2 sticky left-0 bg-white block truncate text-center border-r border-gray-200 w-[150px]`}>
                {workerData.worker?.name || 'No asignado'}
              </td>
              {/* Celda de entradas al sistema con rowspan */}
              {index === 0 && (
                <td 
                  rowSpan={workersInDay.length} 
                  className={`${totalEntradasGlobal === 0 ? 'text-gray-200' : 'text-black font-bold'} text-md bg-white px-2 py-2 text-center border-r border-gray-200 align-middle`}
                >
                  {totalEntradasGlobal}
                </td>
              )}
              {/* Tasa cierre cot. */}
              {index === 0 && (
                <td 
                  rowSpan={workersInDay.length} 
                  className={`${tasaCierreCotizacionGlobal === 0 ? 'text-gray-200' : 'text-black font-bold'} hover:bg-white bg-white px-2 py-2 text-center border-r border-gray-200 align-middle`}
                >
                  {tasaCierreCotizacionGlobal.toFixed(2)}%
                </td>
              )}
              {/* % Tasa cierre cot. individual */}
              <td className={`${tasaCierreCotizacionIndividual === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {tasaCierreCotizacionIndividual.toFixed(2)}%
              </td>
              {/* Tasa cierre venta */}
              {index === 0 && (
                <td 
                  rowSpan={workersInDay.length} 
                  className={`${tasaCierreExitoGlobal === 0 ? 'text-gray-200' : 'text-black font-bold'} hover:bg-white bg-white px-2 py-2 text-center border-r border-gray-200 align-middle`}
                >
                  {tasaCierreExitoGlobal.toFixed(2)}%
                </td>
              )}
              {/* % Tasa cierre venta individual */}
              <td className={`${tasaCierreExitoIndividual === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {tasaCierreExitoIndividual.toFixed(2)}%
              </td>
              <td className={`${totalKPI === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {totalKPI.toFixed(1)}
              </td>
              <td className={`${llamadasPerdidas.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {llamadasPerdidas.length}
              </td>
              <td className={`${llamadasCortas.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {llamadasCortas.length}
              </td>
              <td className={`${llamadasFrias.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {llamadasFrias.length}
              </td>
              <td className={`${llamadasAtendidas.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {llamadasAtendidas.length}
              </td>
              <td className={`${totalLlamadas === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center bg-gray-100 border-r border-gray-200`}>
                {totalLlamadas}
              </td>
              <td className={`${meetAgendados.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {meetAgendados.length}
              </td>
              <td className={`${meetAtendidos.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {meetAtendidos.length}
              </td>
              <td className={`${meetReagendados.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {meetReagendados.length}
              </td>
              <td className={`${meetCancelados.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {meetCancelados.length}
              </td>
              <td className={`${totalMeet === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center bg-gray-100 border-r border-gray-200`}>
                {totalMeet}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Frio').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Frio').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Evaluando opciones').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Evaluando opciones').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Por confirmar depósito').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Por confirmar depósito').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Para el futuro').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Para el futuro').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Curioso').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Curioso').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'No responde').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'No responde').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'No esta en mi ciudad').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'No esta en mi ciudad').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Lead con poco presupuesto').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Lead con poco presupuesto').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'El servicio le parece caro').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'El servicio le parece caro').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Decidió trabajar con otra agencia').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Decidió trabajar con otra agencia').length}
              </td>
              <td className={`${eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Decidió trabajar con un freelancer').length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {eventosCotizacionesEnEspera.filter(e => e.eventSubType === 'Decidió trabajar con un freelancer').length}
              </td>
              <td className={`${quotesEnExito.length === 0 ? 'text-gray-200' : 'text-black font-bold'} px-2 py-2 text-center border-r border-gray-200`}>
                {quotesEnExito.length}
              </td>
              <td className={`${ventasRealizadas === 0 ? 'text-gray-200' : 'text-black font-bold'} truncate bg-green-100 px-2 py-2 text-center border-r border-gray-200 sticky right-[120px] z-10`}>
                S/. {ventasRealizadas.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </td>
              <td className={`${ventasProyectadas === 0 ? 'text-gray-200' : 'text-black font-bold'} truncate bg-white px-2 py-2 text-center border-r border-gray-200 sticky right-0 z-10 min-w-[120px]`}>
                S/. {ventasProyectadas.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </td>
            </tr>
          );
        });
      })()}
    </React.Fragment>
  );
};

export default ItemRowDasboardLeadDia;